import { OAuth2AuthCodePKCE } from '@bity/oauth2-auth-code-pkce'
import { RpcError } from '@protobuf-ts/runtime-rpc'
import { AuthContext } from 'lib/context/app'
import { Code } from 'pbts/google/rpc/code'
import { useContext } from 'react'
import { RequestMetadata } from './rpcts'

export enum Status {
    INIT,
    LOADING,
    OK,
    ERROR,
    ALERTED,
}

export type GetReturn<T> =
    | {
          status: Status.INIT
      }
    | {
          status: Status.OK
          item: T
      }
    | {
          status: Status.ERROR
          error: RpcError
          // TODO degzhaus remove this any
          /* eslint-disable  @typescript-eslint/no-explicit-any */
          request?: any
      }
    | {
          status: Status.LOADING
      }

export const initialGetReturn = <T>(): GetReturn<T> => {
    return {
        status: Status.INIT,
    }
}

export type CreateReturn<T> = GetReturn<T>

export const initialCreateReturn = <T>(): CreateReturn<T> => {
    return {
        status: Status.INIT,
    }
}

export type ListReturn<T> =
    | {
          status: Status.INIT
      }
    | {
          status: Status.OK
          items: Array<T>
          metadata?: RequestMetadata
      }
    | {
          status: Status.ERROR
          error: RpcError
      }
    | {
          status: Status.LOADING
      }

export const initialListReturn = <T>(): ListReturn<T> => {
    return {
        status: Status.INIT,
    }
}

export type EditReturn =
    | {
          status: Status.INIT
      }
    | {
          status: Status.LOADING
      }
    | {
          status: Status.OK
      }
    | {
          status: Status.ERROR
          error: RpcError
      }

export const initialEditReturn = (): EditReturn => {
    return {
        status: Status.INIT,
    }
}

// INIT -> <delete action> -> LOADING -> OK | ERROR
export type DeleteReturn =
    | {
          status: Status.INIT
      }
    | {
          status: Status.LOADING
      }
    | {
          status: Status.OK
      }
    | {
          status: Status.ALERTED
      }
    | {
          status: Status.ERROR
          error: RpcError
      }

export const initialDeleteReturn = (): DeleteReturn => {
    return {
        status: Status.INIT,
    }
}

export type ActionReturn =
    | {
          status: Status.INIT
      }
    | {
          status: Status.LOADING
      }
    | {
          status: Status.OK
      }
    | {
          status: Status.ALERTED
      }
    | {
          status: Status.ERROR
          error: RpcError
      }

export const initialActionReturn = (): ActionReturn => {
    return {
        status: Status.INIT,
    }
}

function isCodeMessageSet(err: unknown): err is RpcError {
    return typeof err === 'object' &&
        err !== null &&
        'code' in err &&
        (err as RpcError).code !== undefined &&
        'message' in err &&
        (err as RpcError).message !== undefined
}

export function useOAuth(): OAuth2AuthCodePKCE | undefined {
    const authContext = useContext(AuthContext)
    return authContext.get('oauth') as OAuth2AuthCodePKCE | undefined
}

export function convertError(err: unknown): RpcError {
    if (err instanceof RpcError) {
        return err
    }
    if (isCodeMessageSet(err)) {
        return new RpcError(err.message, err.code.toString())
    }

    return new RpcError(JSON.stringify(err), codeToString(Code.INTERNAL))
}

export const codeToString = (input: Code): string | undefined => {
    switch (input) {
        case Code.OK:
            return 'OK'
        case Code.CANCELLED:
            return 'CANCELLED'
        case Code.UNKNOWN:
            return 'UNKNOWN'
        case Code.INVALID_ARGUMENT:
            return 'INVALID_ARGUMENT'
        case Code.DEADLINE_EXCEEDED:
            return 'DEADLINE_EXCEEDED'
        case Code.NOT_FOUND:
            return 'NOT_FOUND'
        case Code.ALREADY_EXISTS:
            return 'ALREADY_EXISTS'
        case Code.PERMISSION_DENIED:
            return 'PERMISSION_DENIED'
        case Code.RESOURCE_EXHAUSTED:
            return 'RESOURCE_EXHAUSTED'
        case Code.FAILED_PRECONDITION:
            return 'FAILED_PRECONDITION'
        case Code.ABORTED:
            return 'ABORTED'
        case Code.OUT_OF_RANGE:
            return 'OUT_OF_RANGE'
        case Code.UNIMPLEMENTED:
            return 'UNIMPLEMENTED'
        case Code.INTERNAL:
            return 'INTERNAL'
        case Code.UNAVAILABLE:
            return 'UNAVAILABLE'
        case Code.UNAUTHENTICATED:
            return 'UNAUTHENTICATED'
        // no default
    }
    return undefined
}
